/**
 * TRAJ-8.22: Carbon Trajectory Store
 *
 * Centralized store for trajectory-related data with caching:
 * - Scenarios (previously in projectedPage.vue)
 * - Reference trajectories (previously in projectedChartContainer.vue)
 * - Intensity evolutions (previously in projectedChartContainer.vue)
 * - Company tags (TRAJ-8.27, served by the bootstrap endpoint)
 *
 * TRAJ-8.27: cache persistant. La fraîcheur n'est plus pilotée par l'expiration
 * du TTL mais par l'invalidation explicite lors des mutations. Le TTL ne sert
 * plus que de filet de sécurité pour les données modifiées hors de cet onglet
 * (autre poste, autre session).
 */
import { defineStore } from 'pinia'
import instance from '@/main.js'
import { createInFlightRegistry } from './inFlightRegistry'

const CACHE_TTL = 30 * 60 * 1000 // 30 minutes - filet de sécurité, pas la stratégie principale

// TRAJ-8.27: dédup des requêtes en vol + détection des réponses périmées (cf. inFlightRegistry.js)
const { dedupe, reset: resetInFlight } = createInFlightRegistry()

const AUTH_HEADERS = () => ({
  'token': localStorage.getItem('token'),
  'company': localStorage.getItem('companyId')
})

export const useCarbonTrajectoryStore = defineStore('carbonTrajectory', {
  state: () => ({
    scenarios: [],
    referenceTrajectories: [],
    intensityEvolutions: [],
    // TRAJ-8.24: Add projects state for actionPlansPage
    projects: [],
    // TRAJ-8.27: tags de la société, servis par /trajectory-bootstrap
    tags: [],
    isLoading: {
      scenarios: false,
      referenceTrajectories: false,
      intensityEvolutions: false,
      projects: false,
      bootstrap: false
    },
    lastFetched: {
      scenarios: null,
      referenceTrajectories: null,
      intensityEvolutions: null,
      projects: null,
      tags: null
    }
  }),

  getters: {
    getScenarios: (state) => state.scenarios,
    getReferenceTrajectories: (state) => state.referenceTrajectories,
    getIntensityEvolutions: (state) => state.intensityEvolutions,
    // TRAJ-8.24: Add projects getter
    getProjects: (state) => state.projects,
    // TRAJ-8.27: tags getter
    getTags: (state) => state.tags,

    getScenarioById: (state) => (id) => {
      return state.scenarios.find(s => s.id === id)
    },

    getReferenceTrajectoryById: (state) => (id) => {
      return state.referenceTrajectories.find(t => t.id === id)
    },

    // TRAJ-8.24: Add project getter
    getProjectById: (state) => (id) => {
      return state.projects.find(p => p.id_projet === id)
    },

    isScenariosDataFresh: (state) => {
      if (!state.lastFetched.scenarios) return false
      return (Date.now() - state.lastFetched.scenarios) < CACHE_TTL
    },

    isReferenceTrajectoriesDataFresh: (state) => {
      if (!state.lastFetched.referenceTrajectories) return false
      return (Date.now() - state.lastFetched.referenceTrajectories) < CACHE_TTL
    },

    isIntensityEvolutionsDataFresh: (state) => {
      if (!state.lastFetched.intensityEvolutions) return false
      return (Date.now() - state.lastFetched.intensityEvolutions) < CACHE_TTL
    },

    // TRAJ-8.24: Add projects data freshness check
    isProjectsDataFresh: (state) => {
      if (!state.lastFetched.projects) return false
      return (Date.now() - state.lastFetched.projects) < CACHE_TTL
    }
  },

  actions: {
    /**
     * TRAJ-8.24: Fetch all projects for the current company
     * @param {boolean} skipCache - Force fetch even if cache is fresh
     * @returns {Promise<Array>} - Array of projects
     */
    async fetchProjects(skipCache = false) {
      // Check cache
      // TRAJ-8.27: la fraîcheur se juge sur lastFetched seul. L'ancien test
      // `.length > 0` empêchait de mettre en cache un résultat vide, ce qui
      // provoquait une requête à chaque appel pour les sociétés sans données.
      if (!skipCache && this.isProjectsDataFresh) {
        return this.projects
      }

      // TRAJ-8.27: dédup des requêtes en vol
      return dedupe('projects', async (isStale) => {
        this.isLoading.projects = true
        try {
          const response = await instance.get('/projets', { headers: AUTH_HEADERS() })
          if (isStale()) return this.projects // reset() pendant la requête : ne pas écrire

          this.projects = response.data.data || []
          this.lastFetched.projects = Date.now()

          return this.projects
        } catch (error) {
          console.error('Error fetching projects:', error.response?.data?.message || error.message)
          throw error
        } finally {
          this.isLoading.projects = false
        }
      })
    },

    /**
     * Fetch all scenarios for the current company
     * @param {boolean} skipCache - Force fetch even if cache is fresh
     * @returns {Promise<Array>} - Array of scenarios
     */
    async fetchScenarios(skipCache = false) {
      // Check cache
      // TRAJ-8.27: cf. fetchProjects - un résultat vide est un résultat valide
      if (!skipCache && this.isScenariosDataFresh) {
        return this.scenarios
      }

      // TRAJ-8.27: dédup des requêtes en vol
      return dedupe('scenarios', async (isStale) => {
        this.isLoading.scenarios = true
        try {
          const response = await instance.get('/scenarios', { headers: AUTH_HEADERS() })
          if (isStale()) return this.scenarios // reset() pendant la requête : ne pas écrire

          // Attention: cet endpoint renvoie {"scenarios": [...]}, pas {"data": [...]}
          this.scenarios = response.data.scenarios || []
          this.lastFetched.scenarios = Date.now()

          return this.scenarios
        } catch (error) {
          console.error('Error fetching scenarios:', error.response?.data?.message || error.message)
          throw error
        } finally {
          this.isLoading.scenarios = false
        }
      })
    },

    /**
     * Fetch all reference trajectories for the current company
     * @param {boolean} skipCache - Force fetch even if cache is fresh
     * @returns {Promise<Array>} - Array of reference trajectories
     */
    async fetchReferenceTrajectories(skipCache = false) {
      // Check cache
      // TRAJ-8.27: cf. fetchProjects - un résultat vide est un résultat valide
      if (!skipCache && this.isReferenceTrajectoriesDataFresh) {
        return this.referenceTrajectories
      }

      // TRAJ-8.27: dédup des requêtes en vol
      return dedupe('referenceTrajectories', async (isStale) => {
        this.isLoading.referenceTrajectories = true
        try {
          const response = await instance.get('/reference-trajectories', { headers: AUTH_HEADERS() })
          if (isStale()) return this.referenceTrajectories // reset() pendant la requête : ne pas écrire

          this.referenceTrajectories = response.data.data || []
          this.lastFetched.referenceTrajectories = Date.now()

          return this.referenceTrajectories
        } catch (error) {
          console.error('Error fetching reference trajectories:', error.response?.data?.message || error.message)
          throw error
        } finally {
          this.isLoading.referenceTrajectories = false
        }
      })
    },

    /**
     * Fetch all intensity evolutions for the current company
     * @param {boolean} skipCache - Force fetch even if cache is fresh
     * @returns {Promise<Array>} - Array of intensity evolutions
     */
    async fetchIntensityEvolutions(skipCache = false) {
      // Check cache
      // TRAJ-8.27: cf. fetchProjects - un résultat vide est un résultat valide
      if (!skipCache && this.isIntensityEvolutionsDataFresh) {
        return this.intensityEvolutions
      }

      // TRAJ-8.27: dédup des requêtes en vol
      return dedupe('intensityEvolutions', async (isStale) => {
        this.isLoading.intensityEvolutions = true
        try {
          const response = await instance.get('/intensity-evolutions', { headers: AUTH_HEADERS() })
          if (isStale()) return this.intensityEvolutions // reset() pendant la requête : ne pas écrire

          this.intensityEvolutions = response.data.data || []
          this.lastFetched.intensityEvolutions = Date.now()

          return this.intensityEvolutions
        } catch (error) {
          console.error('Error fetching intensity evolutions:', error.response?.data?.message || error.message)
          throw error
        } finally {
          this.isLoading.intensityEvolutions = false
        }
      })
    },

    /**
     * Fetch all trajectory data
     *
     * TRAJ-8.27: une seule requête sur /trajectory-bootstrap au lieu de trois
     * appels parallèles. Renvoie aussi les tags de la société, ce qui évite une
     * requête dédiée côté projectedPage pour alimenter TagFilterSelector.
     *
     * Les trois endpoints unitaires restent en place et restent utilisés par
     * trajectorySettings.vue.
     *
     * @param {boolean} skipCache - Force fetch even if cache is fresh
     * @returns {Promise<Object>} - { scenarios, referenceTrajectories, intensityEvolutions, tags }
     */
    async fetchAllTrajectoryData(skipCache = false) {
      const allFresh = this.isScenariosDataFresh
        && this.isReferenceTrajectoriesDataFresh
        && this.isIntensityEvolutionsDataFresh
        && this.lastFetched.tags !== null

      if (!skipCache && allFresh) {
        return this.bootstrapPayload()
      }

      // TRAJ-8.27: dédup des requêtes en vol
      return dedupe('bootstrap', async (isStale) => {
        this.isLoading.bootstrap = true
        try {
          const response = await instance.get('/trajectory-bootstrap', { headers: AUTH_HEADERS() })
          if (isStale()) return this.bootstrapPayload() // reset() pendant la requête : ne pas écrire
          const data = response.data || {}
          const now = Date.now()

          this.scenarios = data.scenarios || []
          this.referenceTrajectories = data.reference_trajectories || []
          this.intensityEvolutions = data.intensity_evolutions || []
          this.tags = data.tags || []

          // Les quatre lastFetched doivent être renseignés : sans cela, un appel
          // ultérieur à fetchScenarios() croirait le cache froid et réémettrait
          // une requête - on aurait remplacé 3 requêtes par 4.
          this.lastFetched.scenarios = now
          this.lastFetched.referenceTrajectories = now
          this.lastFetched.intensityEvolutions = now
          this.lastFetched.tags = now

          return this.bootstrapPayload()
        } catch (error) {
          console.error('Error fetching trajectory bootstrap:', error.response?.data?.message || error.message)
          throw error
        } finally {
          this.isLoading.bootstrap = false
        }
      })
    },

    /**
     * TRAJ-8.27: alimenter le cache des évolutions d'intensité depuis une réponse
     * d'écriture (PUT /trajectory-settings renvoie l'état persisté), ce qui évite
     * d'invalider puis de refetcher au retour sur /projected.
     * @param {Array} evolutions - même forme que GET /intensity-evolutions
     */
    setIntensityEvolutions(evolutions) {
      this.intensityEvolutions = Array.isArray(evolutions) ? evolutions : []
      this.lastFetched.intensityEvolutions = Date.now()
    },

    /**
     * TRAJ-8.27: forme de retour commune de fetchAllTrajectoryData
     */
    bootstrapPayload() {
      return {
        scenarios: this.scenarios,
        referenceTrajectories: this.referenceTrajectories,
        intensityEvolutions: this.intensityEvolutions,
        tags: this.tags
      }
    },

    /**
     * TRAJ-8.24: Invalidate projects cache
     * Call when projects are modified
     */
    invalidateProjectsCache() {
      this.lastFetched.projects = null
    },

    /**
     * Invalidate scenarios cache
     * Call when scenarios are modified
     */
    invalidateScenariosCache() {
      this.lastFetched.scenarios = null
    },

    /**
     * Invalidate reference trajectories cache
     * Call when reference trajectories are modified
     */
    invalidateReferenceTrajectoriesCache() {
      this.lastFetched.referenceTrajectories = null
    },

    /**
     * Invalidate intensity evolutions cache
     * Call when intensity evolutions are modified
     */
    invalidateIntensityEvolutionsCache() {
      this.lastFetched.intensityEvolutions = null
    },

    /**
     * TRAJ-8.27: Invalidate company tags cache
     * Call when a tag is created, renamed, recolored or deleted, so that the
     * /projected tag filter (served by /trajectory-bootstrap) reflects it.
     */
    invalidateTagsCache() {
      this.lastFetched.tags = null
    },

    /**
     * Invalidate all trajectory cache
     * Call when:
     * - Modules/PE are modified (affects projections)
     * - Factors are modified
     * - Scenarios/actions are modified
     * - Company parameters are modified (effectifs, CA, intensités)
     */
    invalidateTrajectoryCache() {
      this.lastFetched.scenarios = null
      this.lastFetched.referenceTrajectories = null
      this.lastFetched.intensityEvolutions = null
      this.lastFetched.tags = null
    },

    /**
     * Add a scenario to the local cache
     * @param {Object} scenario - The new scenario object
     */
    addScenarioToCache(scenario) {
      if (!scenario) return
      // Add to beginning of array (most recent first)
      this.scenarios.unshift(scenario)
      this.lastFetched.scenarios = Date.now()
    },

    /**
     * Update a scenario in the local cache
     * @param {Object} updatedScenario - The updated scenario object
     */
    updateScenarioInCache(updatedScenario) {
      const index = this.scenarios.findIndex(s => s.id === updatedScenario.id)
      if (index !== -1) {
        this.scenarios[index] = { ...this.scenarios[index], ...updatedScenario }
      } else {
        this.scenarios.push(updatedScenario)
      }
      this.lastFetched.scenarios = Date.now()
    },

    /**
     * Remove a scenario from the local cache
     * @param {number} scenarioId - The scenario ID to remove
     */
    removeScenarioFromCache(scenarioId) {
      this.scenarios = this.scenarios.filter(s => s.id !== scenarioId)
      this.lastFetched.scenarios = Date.now()
    },

    /**
     * Increment the actions_count of a scenario in the cache
     * @param {number} scenarioId - The scenario ID
     */
    incrementScenarioActionsCount(scenarioId) {
      const scenario = this.scenarios.find(s => s.id === scenarioId)
      if (scenario) {
        scenario.actions_count = (scenario.actions_count || 0) + 1
        this.lastFetched.scenarios = Date.now()
      }
    },

    /**
     * Decrement the actions_count of a scenario in the cache
     * @param {number} scenarioId - The scenario ID
     */
    decrementScenarioActionsCount(scenarioId) {
      const scenario = this.scenarios.find(s => s.id === scenarioId)
      if (scenario && scenario.actions_count > 0) {
        scenario.actions_count = scenario.actions_count - 1
        this.lastFetched.scenarios = Date.now()
      }
    },

    /**
     * Reset all state (e.g., on logout or company switch)
     */
    reset() {
      // TRAJ-8.27: oublier les requêtes en vol ET marquer leurs réponses comme
      // périmées (isStale), sinon une réponse arrivant après un changement de
      // société repeuplerait le store avec les données de la société précédente.
      resetInFlight()
      this.scenarios = []
      this.referenceTrajectories = []
      this.intensityEvolutions = []
      this.projects = []
      this.tags = []
      this.isLoading = {
        scenarios: false,
        referenceTrajectories: false,
        intensityEvolutions: false,
        projects: false,
        bootstrap: false
      }
      this.lastFetched = {
        scenarios: null,
        referenceTrajectories: null,
        intensityEvolutions: null,
        projects: null,
        tags: null
      }
    }
  }
})
